<template>
    <div class="max-w-full rounded-xl shadow bg-white p-4">
        <div v-if="debugStore.debugMode && value.id" class="font-mono mb-4">
            [Table:{{ value.id }}]
        </div>

        <div
            class="col-span-1 border bg-white rounded-lg p-4 shadow-sm mt-3 mb-3"
        >
            <p class="text-gray-700 mb-4">Copy from:</p>
            <reference
                link-text="Select from template"
                :creatable="false"
                :language="language"
                :should-open-new-window="true"
                :disabled="disabled"
                @input="handleCopyTable"
                model-data="TableTemplate"
                :query="{
                    excludeTableId: value.id,
                }"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="w-full grid grid-cols-12 gap-2"
        >
            <Select
                class="col-span-9"
                label="* Table Type"
                name="type_id"
                v-model.number="value.type_id"
                placeholder="Select Table Type"
                :full-width="true"
                unsettable
                :disabled="disabled"
                :options="[
                    { value: TABLE_TYPES.COL1, name: '1 col table' },
                    { value: TABLE_TYPES.COL2, name: '2 col table' },
                    { value: TABLE_TYPES.COL3, name: '3 col table' },
                    { value: TABLE_TYPES.COL4, name: '4 col table' },
                    { value: TABLE_TYPES.COL5, name: '5 col table' },
                    { value: TABLE_TYPES.COL6, name: '6 col table' },
                    { value: TABLE_TYPES.CTA, name: 'CTA table (2 col)' },
                    { value: TABLE_TYPES.COL24, name: '2/4 col table' },
                ]"
                :error="errorMessages?.type_id ? errorMessages?.type_id[0] : null"
            />

            <div
                class="col-span-3 relative"
            >
                <span v-if="resettable && !disabled" v-tooltip="{ content: 'Reset Table', container: false, placement: 'top', html: false, }" class="cursor-pointer block absolute right-0 top-0" @click="resetTable">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="ml-2 w-5 h-5 inline-block">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                </span>
            </div>

            <input-text
                label="Table Heading"
                name="heading_text"
                v-model="value.heading_text"
                class="col-span-9"
                :full-width="true"
                :disabled="disabled"
                :error="errorMessages?.heading_text ? errorMessages?.heading_text[0] : null"
            >
                <p class="text-xs text-gray-600">
                    You can use these available shortcodes in the heading field
                    above: [current_year], [current_month] and [current_abrev_month]
                </p>
            </input-text>
            <Select
                class="col-span-3"
                label="Table Heading Type"
                name="heading_type"
                v-model="value.heading_type"
                :full-width="true"
                placeholder="Select Table Heading Type"
                unsettable
                :disabled="disabled"
                :options="[
                    { value: 'h2', name: 'H2' },
                    { value: 'h3', name: 'H3' },
                    { value: 'h4', name: 'H4' },
                ]"
                :error="errorMessages?.heading_type ? errorMessages?.heading_type[0] : null"
            />

            <input-text
                label="Anchor Text used in Jump Navigation"
                name="anchor_text"
                v-model="value.anchor_text"
                class="col-span-9"
                :full-width="true"
                :disabled="disabled"
                :error="errorMessages?.anchor_text ? errorMessages?.anchor_text[0] : null"
            />

            <input-text
                label="Anchor Id (excluding #)"
                name="anchor_id"
                v-model="value.anchor_id"
                class="col-span-3"
                :full-width="true"
                :disabled="disabled"
                :error="errorMessages?.anchor_id ? errorMessages?.anchor_id[0] : null"
            />

            <div class="col-span-12 grid grid-cols-2 gap-2">
                <div
                    class="col-span-1 border bg-white rounded-lg w-full p-4 shadow-sm mt-3"
                >
                    <p class="text-gray-700 mb-4">Reference Data:</p>

                    <div class="text-red-700 mb-4" v-if="!value.document && value.type_id === TABLE_TYPES.CTA">Required for CTA table.</div>
                    <div class="text-red-700 mb-4" v-if="!value.document && tableHasReferenceCells">Required for Reference cells.</div>

                    <polymorphic-reference
                        :disabled="disabled"
                        :creatable="false"
                        :should-open-new-window=true
                        :language="language"
                        v-model="value.document"
                        :models="selectableModels"
                        :error="[(errorMessages?.document_type? errorMessages?.document_type[0] : null), (errorMessages?.document_id? errorMessages?.document_id[0] : null)].filter(e => !!e).join(', ')"
                    />
                </div>

                <div
                    class="col-span-1 border bg-white rounded-lg p-4 shadow-sm mt-3"
                    v-if="value.document_type === 'App\\Models\\Casino'"
                >
                    <p class="text-gray-700 mb-4">* Bonus:</p>
                    <reference
                        link-text="Link an existing bonus"
                        :creatable="false"
                        :language="language"
                        :should-open-new-window="true"
                        :disabled="disabled"
                        v-model="value.bonus"
                        model-data="Bonus"
                        :error="errorMessages?.bonus_id ? errorMessages?.bonus_id[0] : null"
                        :query="{
                            casino_id: value.document_id,
                        }"
                        :order="{
                            'name': 'asc'
                        }"
                    />
                </div>
            </div>

            <media-reference
                v-if="value.type_id !== TABLE_TYPES.CTA"
                v-model="value.image"
                :language="language"
                :media-types="['Image']"
                class="col-span-2 mt-4"
                :full-width="true"
                name="image"
                :disabled="disabled"
                :error="errorMessages?.image_id ? errorMessages?.bonus_id[0] : null"
            />

            <div class="col-span-12" v-if="value.type_id">
                <table-header
                    v-model="value.headers"
                    :disabled="disabled"
                    :error="errorMessages?.headersArray ?? null"
                />

                <div class="w-full text-red-700" v-if="errorMessages?.headers">
                    {{ errorMessages?.headers?.join(', ') }}
                </div>

                <table-body
                    v-model="value.rows"
                    :language="language"
                    :type-id="value.type_id"
                    :document-type="value.document_type"
                    :disabled="disabled"
                    @reference-label-changed="referenceLabelChanged"
                    @cell-type-changed="cellTypeChanged"
                    :error="errorMessages?.rowsArray ?? null"
                />

                <div class="w-full text-red-700" v-if="errorMessages?.rows">
                    {{ errorMessages?.rows?.join(', ') }}
                </div>

                <Btn
                    @click="handleAddRow"
                    class="mt-3"
                    type="button"
                    content="+ Add New Row"
                    :disabled="disabled"
                    v-if="value.rows.length < maxRows"
                />
            </div>
        </div>
<!--        <error class="mt-3" :error="errors" />-->
    </div>
</template>

<script>
import siteLanguage from '../../config/siteLanguage'
import {v4 as uuidv4} from "uuid";
import {useDebugStore} from "../../stores/debug";
import tableConstants from "../../mixins/tableConstants";
import Reference from "../reference.vue";

export default {
    name: "RocketTable",
    components: {Reference},
    mixins: [tableConstants],
    props: {
        value: {
            type: [String, Object],
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        errors: {
            type: [Array, String],
            required: false,
        },
        resettable: {
            type: Boolean,
            required: false,
            default: true,
        },
        language: {
            type: Object,
            default: () => {
                return {
                    iso: siteLanguage.iso.ENGLISH,
                    id: siteLanguage.language.ENGLISH,
                };
            },
        },
    },
    data() {
        return {
            debugStore: useDebugStore(),
        };
    },
    mounted() {
        if (! this.value?.type_id) {
            this.resetData();
        }

        if (! this.value?.language_id) {
            this.value.language_id = this.language.id;
        }
    },
    methods: {
        handleCopyTable(template) {
            if (template.table) {
                this.$confirm({
                    message: `Copy table from template "${template.name}"?`,
                    button: {
                        no: "No",
                        yes: "Yes",
                    },
                    callback: (confirm) => {
                        if (confirm) {
                            let table = {...template.table};
                            table.id = this.value?.id ?? null;
                            this.$emit('input', table);
                        }
                    },
                });
            }
        },
        resetData() {
            this.value.id = null;
            this.value.type_id = null;
            this.value.heading_text = null;
            this.value.heading_type = null;
            this.value.anchor_id = null;
            this.value.anchor_text = null;
            this.value.document = null;
            this.value.document_type = null;
            this.value.document_id = null;
            this.value.bonus = null;
            this.value.bonus_id = null;
            this.value.image = null;
            this.value.image_id = null;
            this.value.language_id = this.language.id;
            this.value.headers = [];
            this.value.rows = [];
        },
        resetTable() {
            this.$confirm({
                message: `Reset Table?`,
                button: {
                    no: "No",
                    yes: "Yes",
                },
                callback: (confirm) => {
                    if (confirm) {
                        this.resetData();
                    }
                },
            });
        },
        referenceLabelChanged({label, tr, td}) {
            // Reference Value is Selected automatically when Reference Label
            // has been selected in the same row in previous column
            if (this.value.rows[tr].cells[td + 1]) {
                let nextCell = this.value.rows[tr].cells[td + 1];

                // reference value
                if ([
                    this.TABLE_CELL_TYPES.REFERENCE_VALUE,
                    this.TABLE_CELL_TYPES.REFERENCE_VALUE_WITH_LINK,
                ].includes(nextCell.type_id)) {
                    nextCell.reference_value = label;
                }
            }
        },
        cellTypeChanged({typeId, tr, td}) {
            let thisCell = this.value.rows[tr].cells[td];

            // Reference Value is Selected automatically when Reference Label
            // has been selected in the same row in previous column
            if (td > 0 && this.value.rows[tr].cells[td - 1]) {
                let prevCell = this.value.rows[tr].cells[td - 1];

                // reference label
                if (prevCell.type_id === this.TABLE_CELL_TYPES.REFERENCE_LABEL) {
                    // reference value
                    if ([
                        this.TABLE_CELL_TYPES.REFERENCE_VALUE,
                        this.TABLE_CELL_TYPES.REFERENCE_VALUE_WITH_LINK,
                    ].includes(thisCell.type_id)) {
                        thisCell.reference_value = prevCell.reference_label;
                    }
                }
            }

            // if reference label type is selected, automatically set next cell to reference value
            if (thisCell.type_id === this.TABLE_CELL_TYPES.REFERENCE_LABEL && this.value.rows[tr].cells.length > td + 1) {
                let nextCell = this.value.rows[tr].cells[td + 1];

                nextCell.type_id = this.TABLE_CELL_TYPES.REFERENCE_VALUE;
            }
        },
        handleAddRow() {
            if (this.value.rows.length >= this.maxRows) {
                return;
            }

            let newCells = Array(this.cols).fill(null).map(e => this.defaultCell());
            this.value.rows.push({
                id: null,
                cells: newCells,
            });
        },
        setColumns() {
            if (this.value.rows.length) {
                this.value.rows = this.value.rows.map(row => {
                    let cells = row.cells;
                    if (cells.length < this.cols) {
                        let newCells = Array(this.cols - cells.length)
                            .fill(null)
                            .map(e => this.defaultCell());
                        cells = [...cells, ...newCells];
                    } else if (cells.length > this.cols) {
                        cells = cells.slice(0, this.cols);
                    }
                    row.cells = cells;
                    return row;
                });
            }

            if (this.value.headers.length < this.cols) {
                this.value.headers = [
                    ...this.value.headers,
                    ...Array(this.cols - this.value.headers.length)
                        .fill(null)
                ];
            } else if (this.value.headers.length > this.cols) {
                this.value.headers = this.value.headers.slice(0, this.cols);
            }
        },
        defaultCell() {
            return {
                type_id: this.TABLE_CELL_TYPES.TEXT,
                text: null,
                bold: null,
                icon: null,
                icon_id: null,
                logo: null,
                logo_id: null,
                link_id: null,
                link: null,
                cta_style: null,
                reference_label: null,
                reference_value: null,
                with_icon: null,
                key: uuidv4(),
            };
        },
        mergeErrorMessages (errors) {
            let merged = {};

            /**
             * We need to merge error messages from this:
             * rows.0.cells.0.link:['rows.0.cells.0.link document is required for internal links.']
             * rows.0.cells.1.text:['The rows.0.cells.1.text field is required when rows.0.cells.1.type_id is 1.']
             *
             * to this:
             * rowsArray: [
             *      {
             *          cells.0.link:['rows.0.cells.0.link document is required for internal links.']
             *          cells.1.text:['The rows.0.cells.1.text field is required when rows.0.cells.1.type_id is 1.']
             *      }
             * ]
             *
             * and
             * headers.0:['The headers.0 must be a string.']
             * headers.1:['The headers.1 must be a string.']
             *
             * to this:
             * headersArray: [
             *     ['The headers.0 must be a string.']
             *     ['The headers.1 must be a string.']
             * ]
             *
             * we need to rename the props to headersArray because there can be a standalone error named headers
             */

            let regex = /^(?<key>[A-Za-z_$][\w$]+)\.(?<index>\d+)\.?(?<suffix>.*)/;

            for (let [key, value] of Object.entries(errors)) {
                let matches = key.match(regex);
                if (matches) {
                    let prop = `${matches.groups.key}Array`;

                    if (! merged[prop]) {
                        merged[prop] = [];
                    }

                    if (matches.groups.suffix) {
                        if (! merged[prop][matches.groups.index]) {
                            merged[prop][matches.groups.index] = {};
                        }

                        merged[prop][matches.groups.index][matches.groups.suffix] = value;
                    } else {
                        merged[prop][matches.groups.index] = value;
                    }
                } else {
                    merged[key] = value;
                }
            }

            /**
             * Now we need to turn this
             * rowsArray: [
             *      {
             *          "cells.0.link":['rows.0.cells.0.link document is required for internal links.'],
             *          "cells.1.text":["The rows.0.cells.1.text field is required when rows.0.cells.1.type_id is 1."],
             *          "cells.2":["Text or icon must be present."]
             *      }
             * ]
             *
             * into this
             *
             * rowsArray: [
             *      {
             *          cellsArray: [
             *              {
             *                  link:['rows.0.cells.0.link document is required for internal links.']
             *              },
             *              {
             *                  text:['The rows.0.cells.1.text field is required when rows.0.cells.1.type_id is 1.']
             *              },
             *              {
             *                  $:["Text or icon must be present."]
             *              }
             *          ],
             *      }
             * ],
             */

            if (merged.rowsArray) {
                merged.rowsArray = merged.rowsArray.map((row) => {
                    let mapped = {};
                    for (let [key, value] of Object.entries(row)) {
                        let matches = key.match(regex);

                        if (matches) {
                            let suffix = '$';

                            if (matches.groups.suffix) {
                                suffix = matches.groups.suffix;
                            }

                            let prop = `${matches.groups.key}Array`;

                            if (! mapped[prop]) {
                                mapped[prop] = [];
                            }

                            if (! mapped[prop][matches.groups.index]) {
                                mapped[prop][matches.groups.index] = {};
                            }

                            mapped[prop][matches.groups.index][suffix] = value;
                        } else {
                            mapped[key] = value;
                        }
                    }

                    return mapped;
                });
            }

            return merged;
        },
    },
    computed: {
        cols() {
            switch (this.value.type_id) {
                case this.TABLE_TYPES.COL1: return 1;
                case this.TABLE_TYPES.COL2: return 2;
                case this.TABLE_TYPES.COL3: return 3;
                case this.TABLE_TYPES.COL4: return 4;
                case this.TABLE_TYPES.COL5: return 5;
                case this.TABLE_TYPES.COL6: return 6;
                case this.TABLE_TYPES.CTA: return 2;
                case this.TABLE_TYPES.COL24: return 2;
                default: return 0;
            }
        },
        selectableModels() {
            if (this.value.type_id === this.TABLE_TYPES.CTA) {
                // CTA table can only select casinos
                return ['Casino'];
            }

            return [
                'Casino',
                'Game',
                'Slot',
                'Software',
                'Payment Method',
                'License',
            ];
        },
        maxRows() {
            return 20;
        },
        errorMessages() {
            if (!this.errors) return null;

            try {
                let errors = JSON.parse(this.errors);

                let merged = this.mergeErrorMessages(errors);

                return merged;
            }
            catch (e) {
                console.error(e);
            }

            return null;
        },
        tableHasReferenceCells() {
            return this.value.rows?.filter((row) => {
                return row.cells?.filter((cell) => {
                    return [
                        this.TABLE_CELL_TYPES.REFERENCE_LABEL,
                        this.TABLE_CELL_TYPES.REFERENCE_VALUE,
                        this.TABLE_CELL_TYPES.REFERENCE_VALUE_WITH_LINK,
                    ].includes(cell.type_id);
                })?.length ?? 0 > 0;
            })?.length ?? 0 > 0;
        },
    },
    watch: {
        'value.type_id': function (newVal, oldVal) {
            // add warning popup

            this.setColumns();

            if (this.value.type_id === this.TABLE_TYPES.CTA) {
                this.value.image = null;

                if (this.value.document_type !== 'App\\Models\\Casino') {
                    // CTA table can only select casinos
                    this.value.document = null;
                }
            }
        },
        'value.document': function (newVal) {
            this.value.document_type = newVal?.model_name ?? null;
            this.value.document_id = newVal?.id ?? null;

            if (this.value.document_type !== 'App\\Models\\Casino') {
                this.value.bonus = null;
            }
        },
        'value.bonus': function (newVal) {
            this.value.bonus_id = newVal?.id ?? null;
        },
        'value.image': function (newVal) {
            this.value.image_id = newVal?.id ?? null;
        },
    },
};
</script>
